import { DateTime } from 'luxon';
import { CardProps } from '@la/ds-ui-components';
import { formatAsCurrency } from '@la/ds-ui-components';
import { Division } from '@la/types';
import { formatDateUsingShortMonthName } from 'lib/utils/dateUtils';
import { DivisionUnavailableReason } from '../../../Wizard.types';
import { DivisionCardsProps } from '../../DivisionCards/DivisionCards';
import { DivisionCardProps } from '../DivisionCard';
import * as S from './DivisionCardHeader.styles';

export const NUMBER_OF_SPOTS_WARNING_AMOUNT = 5;

type DivisionCardHeaderProps = Pick<
  Division,
  'cost' | 'name' | 'registrationEndDate' | 'registrationStartDate'
> &
  Pick<DivisionCardProps, 'cardState'> &
  Pick<CardProps, 'variant'> & {
    cardLabelId: string;
    numberOfSpotsLeft?: number;
    costLabel: DivisionCardsProps['costLabel'];
  };

export function DivisionCardHeader({
  cardLabelId,
  cardState,
  cost,
  name,
  numberOfSpotsLeft,
  registrationStartDate,
  registrationEndDate,
  variant,
  costLabel,
}: DivisionCardHeaderProps) {
  let cardHeaderRightSection;
  if (cardState.value === 'unavailable') {
    cardHeaderRightSection = (
      <S.DivisionCardHeaderUnavailable>
        {getUnavailableMessage({
          reasonUnavailable: cardState.reasonUnavailable,
          registrationDates: {
            start: registrationStartDate,
            end: registrationEndDate,
          },
        })}
      </S.DivisionCardHeaderUnavailable>
    );
  } else {
    cardHeaderRightSection = (
      <S.CardHeaderRightSection>
        <div>{formatAsCurrency(cost ? Number(cost) : 0)}</div>
        {costLabel ? (
          <S.CardHeaderCostLabel variant="ui" size="medium">
            {costLabel}
          </S.CardHeaderCostLabel>
        ) : null}
      </S.CardHeaderRightSection>
    );
  }

  return (
    <S.DivisionCardHeader $variant={variant}>
      <S.TitleAndSpotsLeft>
        <div id={cardLabelId}>{name}</div>
        <DivisionSpotsLeftMessage numberOfSpotsLeft={numberOfSpotsLeft} />
      </S.TitleAndSpotsLeft>
      {cardHeaderRightSection}
    </S.DivisionCardHeader>
  );
}

function DivisionSpotsLeftMessage({
  numberOfSpotsLeft,
}: {
  numberOfSpotsLeft?: number;
}) {
  if (numberOfSpotsLeft) {
    if (numberOfSpotsLeft < 0) {
      numberOfSpotsLeft = 0;
    }
    const showWarning = numberOfSpotsLeft <= 5 && numberOfSpotsLeft !== 0;
    const spotsLeftText = `${
      numberOfSpotsLeft === 1 ? '1 spot' : `${numberOfSpotsLeft} spots`
    } left`;

    if (showWarning) {
      return (
        <S.SpotsLeftWarningMessage>{spotsLeftText}!</S.SpotsLeftWarningMessage>
      );
    } else {
      return <S.SpotsLeftMessage>{spotsLeftText}</S.SpotsLeftMessage>;
    }
  }

  return null;
}

function getUnavailableMessage({
  reasonUnavailable,
  registrationDates,
}: {
  reasonUnavailable: DivisionUnavailableReason;
  registrationDates: {
    start?: string;
    end?: string;
  };
}) {
  switch (reasonUnavailable) {
    case 'before-registration-period-opens':
      return (
        <>
          Registration opens{' '}
          <S.RegistrationStartDate>
            {formatDateUsingShortMonthName(
              DateTime.fromISO(registrationDates.start!)
            )}
          </S.RegistrationStartDate>
        </>
      );
    case 'after-registration-period-ends':
      return 'Registration closed';
    case 'at-capacity':
      return 'At capacity';
  }

  return null;
}
